import React, { useEffect, useMemo } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { isNaN } from 'lodash';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import Button from '../../common/Button';
import { type TypeScenarioVectorCriteria } from '../../common/dataTypes';
import { useUpdateVector } from '../../hooks';
import { useGetIFSConfig } from '../../hooks/tanstack/useDatasetsHook';
import { useGetDataTableByName } from '../../hooks/tanstack/useDataTablesHook';
import { useGetScenarioData } from '../../hooks/tanstack/useScenariosHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import { useCreateVector } from '../../hooks/tanstack/useVectorHook';
import cn from '../../utils/cn';
import { transformToLabel, transformToValue } from '../../utils/helpers';
import FormInput from '../form/FormInput';
import FormSelect from '../form/FormSelect';

type CreateScenarioFormDialogProps = {
  open: boolean;
  title: string;
  trigger?: {
    label: React.ReactNode;
    style?: React.CSSProperties;
    onClick?: () => void;
  };
  showTrigger?: boolean;
  defaultValues?: Record<string, any>;
  handleOpenDialog: () => void;
  handleCloseDialog: () => void;
  handleSubmitDialog?: () => void;
  dataset?: string;
  dataTable?: string;
};

const CreateVectorSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, { message: 'Vector name is required' }),
  description: z.string().optional(),
  aircraft_type: z
    .string({
      required_error: 'Select an aircraft type',
    })
    .optional(),
  crew_type: z
    .string({
      required_error: 'Select a crew type',
    })
    .optional(),
  years_of_services: z.string().or(z.number()).optional(),
  airport_code: z.string().or(z.number()).optional(),
  start_period: z.string().min(1, {
    message: 'Select a start period',
  }),
  end_period: z.string().min(1, {
    message: 'Select an end period',
  }),
  period: z
    .enum(['base', 'prior'], {
      required_error: 'Select a period',
    })
    .default('base'),
  action: z.enum(
    ['increase', 'decrease', 'set', 'upper_bound', 'lower_bound'],
    {
      required_error: 'Select an action',
    }
  ),
  new_value: z.number().optional().nullable(),
  type: z.enum(['delta', 'percentage'], {
    required_error: 'Select a type',
  }),
  amount: z.coerce.number().gte(0, 'Value must be greater than or equal to 0'),
});

type CreateVectorSchemaType = z.infer<typeof CreateVectorSchema>;

export const VECTOR_DIRECTION_INCREASE = 'increase';
export const VECTOR_DIRECTION_DECREASE = 'decrease';
export const VECTOR_DIRECTION_SET = 'set';
export const VECTOR_TYPE_DELTA = 'delta';
export const VECTOR_DIRECTION_UPPER_BOUND = 'upper_bound';
export const VECTOR_DIRECTION_LOWER_BOUND = 'lower_bound';
export const VECTOR_TYPE_PERCENTAGE = 'percentage';

const CreateVectorFormDialog = ({
  open,
  title,
  trigger,
  showTrigger = true,
  defaultValues,
  handleOpenDialog,
  handleCloseDialog,
  handleSubmitDialog,
}: CreateScenarioFormDialogProps) => {
  const form = useForm<CreateVectorSchemaType>({
    resolver: zodResolver(CreateVectorSchema),
    defaultValues: {
      action: VECTOR_DIRECTION_INCREASE,
      type: VECTOR_TYPE_PERCENTAGE,
      amount: 0,
      start_period: '',
      end_period: '',
      period: 'base',
      name: '',
      description: '',
    },
  });

  const { handleSubmit, watch, reset, setValue, formState } = form;

  const { isDirty } = formState;

  const [searchParams] = useSearchParams();

  const { projectId, orgId, inputTableName, modelId } = useGetRouteParams();

  const scenarioId = Number(searchParams.get('scenario_id'));

  const { data: dataTable } = useGetDataTableByName(
    {
      dataTableName: inputTableName ?? '',
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!inputTableName && !!orgId && !!projectId && !!modelId,
    }
  );

  const { data: ifsConfig } = useGetIFSConfig(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const { data: scenarioData, isRefetching: isRefetchingScenarioData } =
    useGetScenarioData(
      {
        scenarioId,
        orgId,
        modelId,
        projectId,
        dataName: '',
      },
      {
        enabled: !!scenarioId && !!orgId && !!projectId && !!modelId,
      }
    );

  const { mutate: createVector, isPending: isCreatingVector } = useCreateVector(
    {
      onError(error) {
        console.error(error);
      },
      onSuccess() {
        handleCloseDialogCreateVector();
      },
    }
  );

  const { mutate: updateVector, isPending: isUpdatingVector } = useUpdateVector(
    {
      onSuccess() {
        handleSubmitDialog?.();
        handleCloseDialogCreateVector();
      },
    }
  );

  const granularity = ifsConfig?.granularity;

  const filterableColumns = dataTable?.filterable_columns ?? [];

  const periods = useMemo(
    () =>
      ifsConfig?.periods.map((p) => ({
        label: p,
        value: p,
      })) ?? [],
    [ifsConfig?.periods]
  );

  const action = watch('action');

  const filterableFormFields = useMemo(() => {
    const data = scenarioData;

    if (!data) {
      return;
    }

    return filterableColumns.reduce(
      (acc, cur) => {
        const curData = Array.from(
          new Set(Object.values(data[cur] ?? {}))
        ) as string[];

        if (cur === 'period') {
          acc['from'] = Object.keys(data).filter((d) => !isNaN(Number(d)));
          acc['to'] = Object.keys(data).filter((d) => !isNaN(Number(d)));

          return acc;
        }

        acc[cur] = ['all'].concat(curData);

        return acc;
      },
      {} as Record<string, string[]>
    );
  }, [scenarioData, filterableColumns]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
      return;
    }
  }, [defaultValues]);

  useEffect(() => {
    if (
      action === VECTOR_DIRECTION_SET ||
      action === VECTOR_DIRECTION_UPPER_BOUND ||
      action === VECTOR_DIRECTION_LOWER_BOUND
    ) {
      setValue('type', 'delta');
    }
  }, [action]);

  useEffect(() => {
    if (periods.length === 0 || watch('start_period')) {
      return;
    }

    setValue('start_period', periods[0].value);
  }, [periods, watch('start_period')]);

  useEffect(() => {
    if (defaultValues) {
      return;
    }

    if (filterableFormFields) {
      Object.entries(filterableFormFields).forEach(([key, value]) => {
        if (key === 'to') {
          setValue(
            key as keyof CreateVectorSchemaType,
            value[value.length - 1]
          );
          return;
        }
        setValue(key as keyof CreateVectorSchemaType, value[0]);
      });
    }
  }, [filterableFormFields, defaultValues]);

  const handleOpenDialogCreateVector = () => {
    trigger?.onClick?.();
    handleOpenDialog();
  };

  const handleCloseDialogCreateVector = () => {
    handleCloseDialog();

    setTimeout(() => {
      reset();
    }, 1000);
  };

  const onSubmit: SubmitHandler<CreateVectorSchemaType> = () => {
    if (!isDirty) {
      return;
    }

    const {
      start_period,
      end_period,
      action,
      type,
      amount,
      period,
      name,
      description,
    } = watch();

    const tempCriterias = filterableColumns.reduce((acc, cur) => {
      const tempValue = {
        field_name: '',
        value: '',
        value_from: '',
        value_to: '',
      } as TypeScenarioVectorCriteria;

      tempValue.field_name = cur;
      tempValue.value = watch()[cur as keyof CreateVectorSchemaType];
      tempValue.operator = 'eq';

      acc.push(tempValue);

      return acc;
    }, [] as TypeScenarioVectorCriteria[]);

    const tempVector: any = {
      name,
      description,
      scenario: Number(scenarioId),
      delta: undefined,
      delta_percentage: undefined,
      start_period,
      end_period,
      period,
      new_value: undefined,
      criterias: tempCriterias,
    };

    if (action === VECTOR_DIRECTION_INCREASE) {
      if (type === VECTOR_TYPE_DELTA) {
        tempVector.delta = amount;
      } else if (type === VECTOR_TYPE_PERCENTAGE) {
        tempVector.delta_percentage = amount;
      }
    } else if (action === VECTOR_DIRECTION_DECREASE) {
      if (type === VECTOR_TYPE_DELTA) {
        tempVector.delta = -amount;
      } else if (type === VECTOR_TYPE_PERCENTAGE) {
        tempVector.delta_percentage = -amount;
      }
    } else if (action === VECTOR_DIRECTION_SET) {
      tempVector.new_value = amount;
    } else if (action === VECTOR_DIRECTION_UPPER_BOUND) {
      tempVector.upper_bound = amount;
    } else if (action === VECTOR_DIRECTION_LOWER_BOUND) {
      tempVector.lower_bound = amount;
    }

    if (defaultValues) {
      updateVector({
        orgId,
        projectId,
        modelId,
        scenarioId,
        vectorId: Number(defaultValues.id),
        data: tempVector,
      });
      return;
    }

    createVector({
      orgId,
      projectId,
      scenarioId,
      modelId,
      data: tempVector,
    });
  };

  const isLoading =
    isCreatingVector || isUpdatingVector || isRefetchingScenarioData;

  const startPeriod = watch('start_period');

  const endPeriods = useMemo(
    () =>
      startPeriod
        ? periods.filter((p) => {
            if (granularity === 'monthly') {
              const [month, year] = p.value.split('-');
              const [startMonth, startYear] = startPeriod.split('-');

              if (year === startYear) {
                return Number(month) > Number(startMonth);
              }
              return year > startYear;
            } else if (granularity === 'weekly') {
              const [week, month, year] = p.value.split('-');
              const [startWeek, startMonth, startYear] = startPeriod.split('-');

              if (year === startYear) {
                if (month === startMonth) {
                  return Number(week) > Number(startWeek);
                }
                return Number(month) > Number(startMonth);
              }

              return Number(year) > Number(startYear);
            }

            return Number(p.value) > Number(startPeriod);
          })
        : periods,
    [startPeriod, periods, granularity]
  );

  useEffect(() => {
    if (watch('end_period')) {
      return;
    }

    if (endPeriods.length > 0) {
      setValue('end_period', endPeriods[endPeriods.length - 1].value);
    }
  }, [endPeriods, watch('end_period')]);

  return (
    <>
      {showTrigger && (
        <Button
          variant="contained_red"
          onClick={handleOpenDialogCreateVector}
          className="text-start cursor-pointer"
          disabled={isLoading}
          style={trigger?.style}
        >
          {trigger ? (
            trigger.label
          ) : (
            <div className="flex cursor-pointer gap-2 items-center">
              <Plus width={20} height={20} />
              <p>New</p>
            </div>
          )}
        </Button>
      )}
      <Dialog
        open={!!open}
        onClose={!isLoading ? handleCloseDialogCreateVector : undefined}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="w-full">
          <h2 className="pt-2 text-xl font-bold">{title}</h2>
        </DialogTitle>
        <DialogContent>
          <div className="z-50">
            <FormProvider {...form}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="mb-2 space-y-6"
              >
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-2">
                      <FormInput name="name" label="Name" />
                      <FormInput
                        name="description"
                        label="Description"
                        multiline
                        rows={3}
                        placeholder="(Optional)"
                      />
                    </div>
                    <div className="flex gap-2 col-span-2">
                      <FormSelect
                        name="start_period"
                        label="From"
                        fullWidth
                        defaultValue={periods[0]?.value}
                        options={periods}
                      />
                      <FormSelect
                        name="end_period"
                        label="To"
                        fullWidth
                        defaultValue={periods[periods.length - 1]?.value}
                        options={endPeriods}
                      />
                    </div>
                    {filterableFormFields &&
                      Object.entries(filterableFormFields).map(
                        ([key, value]) => (
                          <div
                            key={key}
                            className={cn('col-span-2', {
                              'col-span-1': key === 'from' || key === 'to',
                            })}
                          >
                            <FormSelect
                              defaultValue={
                                key === 'to'
                                  ? value[value.length - 1]
                                  : transformToValue(value[0])
                              }
                              label={transformToLabel(key)}
                              name={key}
                              options={value
                                .filter((v) => v)
                                ?.map((v) => ({
                                  label: v,
                                  value: v,
                                }))}
                            />
                          </div>
                        )
                      )}
                  </div>
                  <div className="flex gap-3">
                    <FormSelect
                      name="action"
                      label="Action"
                      defaultValue="increase"
                      options={[
                        {
                          label: 'INCREASE',
                          value: 'increase',
                        },
                        {
                          label: 'DECREASE',
                          value: 'decrease',
                        },
                        {
                          label: 'SET VALUE',
                          value: 'set',
                        },
                        {
                          value: 'upper_bound',
                          label: 'SET UPPER BOUND',
                        },
                        {
                          value: 'lower_bound',
                          label: 'SET LOWER BOUND',
                        },
                      ]}
                    />
                    <FormSelect
                      name="period"
                      label="Period"
                      defaultValue="base"
                      options={[
                        {
                          label: 'Base Period(Default)',
                          value: 'base',
                        },
                        {
                          label: 'Prior Period',
                          value: 'prior',
                        },
                      ]}
                    />
                  </div>
                  <div className="flex gap-3">
                    <FormSelect
                      name="type"
                      label="Type"
                      disabled={
                        watch('action') === VECTOR_DIRECTION_SET ||
                        watch('action') === VECTOR_DIRECTION_UPPER_BOUND ||
                        watch('action') === VECTOR_DIRECTION_LOWER_BOUND
                      }
                      defaultValue="percentage"
                      options={[
                        {
                          label: 'Percentage',
                          value: 'percentage',
                        },
                        {
                          label: 'Fixed amount',
                          value: 'delta',
                        },
                      ]}
                    />
                    <FormInput
                      name="amount"
                      label="Value"
                      type="number"
                      inputProps={{
                        step: '0.01',
                      }}
                    />
                  </div>
                </div>
                <div className="flex-end flex justify-end gap-3">
                  <Button
                    onClick={handleCloseDialogCreateVector}
                    variant="outlined"
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading || !isDirty}
                    variant="contained_blue"
                  >
                    Save
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateVectorFormDialog;
